import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Paper,
    TextField,
    IconButton,
    Avatar,
    Typography,
    Badge,
    InputAdornment,
    Chip,
} from "@mui/material";
import {
    Send as SendIcon,
    Search as SearchIcon,
    Circle as CircleIcon,
    AttachFile as AttachFileIcon,
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    ChatBubbleOutline as ChatBubbleOutlineIcon,
} from "@mui/icons-material";
import { useSocket } from "../../services/SocketProvider";
import { fDateTime } from "../../utils/formatTime";
import { capitalizeFirstLetter } from "../../utils/helpers";
import useResponsive from "../../hooks/useResponsive";
import { IMAGE_BASE } from "../../utils/constants";
import "../../styles/custom.css";

export default function ChatSupport() {
    const { socket, isConnected, socketOn, socketEmit } = useSocket();
    const isMobile = useResponsive('down', 'md');
    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [pagination, setPagination] = useState(null);
    const messagesEndRef = useRef(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Fetch conversations from backend via socket
    useEffect(() => {
        if (!socket || !isConnected) return;

        // Prepare payload
        const payload = {
            page: page,
            limit: limit,
            search: searchQuery,
        };

        socketEmit("inbox_support_conversations", payload);

        const cleanupConversations = socketOn("support_conversations_response", (response) => {


            if (response && response.status && response.data) {
                const mappedConversations = response.data.map((conv) => ({
                    id: conv.receiver?._id || conv.receiverId,
                    userName: `${conv.receiver?.firstName || ''} ${conv.receiver?.lastName || ''}`.trim() || 'Unknown User',
                    userEmail: conv.receiver?.email || '',
                    lastMessage: conv.lastMessage?.message || '',
                    timestamp: conv.lastMessage?.createdAt ? new Date(conv.lastMessage.createdAt) : new Date(),
                    unread: conv.unreadCount || 0,
                    online: false,
                    receiverId: conv.receiverId,
                    receiverType: conv.receiverType,
                }));

                setConversations(mappedConversations);

                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        });

        return () => {
            cleanupConversations();
        };
    }, [socket, isConnected, socketOn, socketEmit, page, limit, searchQuery]);

    useEffect(() => {
        if (!socket || !isConnected) return;
        const cleanupNewMessage = socketOn("newMessage", (message) => {
            const mappedMessage = {
                ...message,
                attachments: message.attachments || [],
            };
            setMessages((prev) => [...prev, mappedMessage]);
        });

        const cleanupTyping = socketOn("userTyping", (data) => {

        });

        const cleanupConversationMessages = socketOn("support_conversation_response", (response) => {

            if (response && response.status === true && response.data && response.data.messages && Array.isArray(response.data.messages)) {
                const mappedMessages = response.data.messages.map((msg) => ({
                    id: msg._id || msg.id,
                    text: msg.message || msg.text || '',
                    sender: msg.senderType === 'Admin' || msg.senderType === 'admin' || msg.senderType === 'subAdmin' ? 'admin' : 'user',
                    timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
                    senderId: msg.senderId,
                    senderType: msg.senderType,
                    isRead: msg.isRead,
                    messageType: msg.messageType,
                    attachments: msg.attachments || [],
                }));
                mappedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                setMessages(mappedMessages);

                if (response.data.pagination) {

                }
            } else if (response && response.status === false) {
                console.error("Backend Error:", response.message);
                setMessages([{
                    id: 'error-1',
                    text: `Error loading messages: ${response.message || 'Unknown error'}`,
                    sender: 'system',
                    timestamp: new Date(),
                }]);
            } else {
                setMessages([{
                    id: 'error-2',
                    text: 'Unable to load messages. Please try again.',
                    sender: 'system',
                    timestamp: new Date(),
                }]);
            }
        });

        const cleanupSentMessage = socketOn("support_message_response", (response) => {


            if (response && response.status === true && response.data) {
                const sentMessage = {
                    id: response.data._id,
                    text: response.data.message,
                    sender: 'admin',
                    timestamp: response.data.createdAt ? new Date(response.data.createdAt) : new Date(),
                    senderId: response.data.senderId,
                    senderType: response.data.senderType,
                    isRead: response.data.isRead,
                    messageType: response.data.messageType,
                    attachments: response.data.attachments || [],
                };

                setMessages((prev) => {
                    const filtered = prev.filter(msg => !msg.id.toString().startsWith('temp-'));
                    return [...filtered, sentMessage];
                });
            } else {
                console.error("Failed to send message:", response?.message);
            }
        });

        return () => {
            cleanupNewMessage();
            cleanupTyping();
            cleanupConversationMessages();
            cleanupSentMessage();
        };
    }, [socket, isConnected, socketOn]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate image file type
        const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
        const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            alert('Only image files are allowed (PNG, JPG, JPEG, GIF, WEBP)');
            e.target.value = '';
            return;
        }

        setSelectedImage(file);
        setImagePreview(URL.createObjectURL(file));
        e.target.value = '';
    };

    const handleRemoveImage = () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        setSelectedImage(null);
        setImagePreview(null);
    };

    const convertImageToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleSendMessage = async () => {
        if ((!messageInput.trim() && !selectedImage) || !selectedChat) return;

        let attachments = [];
        if (selectedImage) {
            try {
                const base64Image = await convertImageToBase64(selectedImage);
                attachments = [{
                    fileType: 'image',
                    base64: base64Image,
                    fileName: selectedImage.name,
                }];
            } catch (error) {
                console.error('Error converting image to base64:', error);
                alert('Error processing image. Please try again.');
                return;
            }
        }

        const payload = {
            message: messageInput.trim() || '',
            receiverId: selectedChat.receiverId || selectedChat.id,
            messageType: selectedImage ? "image" : "text",
            attachments: attachments,
        };

        if (socket && isConnected) {
            socketEmit("send_support_message", payload);
            const optimisticMessage = {
                id: `temp-${Date.now()}`,
                text: messageInput.trim() || '',
                sender: "admin",
                timestamp: new Date(),
                senderId: selectedChat.receiverId,
                senderType: "Admin" || "subAdmin",
                messageType: selectedImage ? "image" : "text",
                attachments: attachments,
            };

            setMessages((prev) => [...prev, optimisticMessage]);
            setMessageInput("");
            handleRemoveImage();
        } else {
            console.error("Cannot send message - Socket not connected");
        }
    };

    const handleChatSelect = (chat) => {
        setSelectedChat(chat);
        setMessages([]);
        if (socket && isConnected && chat) {
            const payload = {
                receiverId: chat.receiverId || chat.id,
                pageNumber: 1,
                pageSize: 50,
            };
            socketEmit("get_support_conversation", payload);
        }
    };

    const handleBackToList = () => {
        setSelectedChat(null);
        setMessages([]);
        handleRemoveImage();
    };

    const filteredConversations = conversations.filter(
        (conv) =>
            conv.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            conv.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box className="chat-support-container">
            <Card className="chat-support-card">
                <title>Chat Support - Ziskat</title>
                <CardHeader
                    title="Chat Support"
                    className="chat-support-header"
                />
                <CardContent className="chat-support-content chat-support-content-full">
                    {filteredConversations.length === 0 ? (
                        <Box className="chat-support-empty-state">
                            <Box className="chat-support-empty-icon-container">
                                <ChatBubbleOutlineIcon className="chat-support-empty-icon" />
                            </Box>
                            <Typography variant="h4" className="chat-support-empty-title">
                                No User Found
                            </Typography>
                            <Typography variant="body1" className="chat-support-empty-text">
                                No conversations available at the moment. 
                                {/* New conversations will appear here when users start chatting. */}
                            </Typography>
                        </Box>
                    ) : (
                        <Box className="chat-support-conversations-container">
                            {/* Conversations List */}
                            <Box
                                className="chat-support-conversations-list"
                                style={{ display: isMobile && selectedChat ? "none" : "flex" }}
                            >
                                <Box className="chat-support-search-container">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="chat-support-search-input"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon style={{ color: "rgba(0, 0, 0, 0.6)" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                                <Box className="chat-support-conversations-scroll">
                                    {filteredConversations.map((conv) => (
                                        <Paper
                                            key={conv.id}
                                            onClick={() => handleChatSelect(conv)}
                                            className={`chat-support-conversation-item ${selectedChat?.id === conv.id ? 'selected' : ''}`}
                                            elevation={0}
                                        >
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                                    variant="dot"
                                                >
                                                    <Avatar className="chat-support-conversation-avatar">
                                                        {capitalizeFirstLetter(conv.userName).charAt(0)}
                                                    </Avatar>
                                                </Badge>
                                                <Box flex={1} style={{ minWidth: 0 }}>
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        style={{ marginBottom: 4 }}
                                                    >
                                                        <Typography 
                                                            variant="subtitle2" 
                                                            noWrap
                                                            className={`chat-support-conversation-name ${selectedChat?.id === conv.id ? 'selected' : ''}`}
                                                        >
                                                            {capitalizeFirstLetter(conv.userName)}
                                                        </Typography>
                                                        {conv.unread > 0 && (
                                                            <Chip
                                                                label={conv.unread}
                                                                size="small"
                                                                color="primary"
                                                                className="chat-support-conversation-unread"
                                                            />
                                                        )}
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        className="chat-support-conversation-message"
                                                    >
                                                        {conv.lastMessage?.length > 35
                                                            ? conv.lastMessage.slice(0, 35) + "..."
                                                            : conv.lastMessage || "No messages"}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        className="chat-support-conversation-time"
                                                    >
                                                        {fDateTime(conv.timestamp)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>

                            {/* Chat Area */}
                            <Box 
                                className={`chat-support-chat-area ${isMobile && !selectedChat ? 'hidden' : ''}`}
                            >
                                {selectedChat ? (
                                    <>
                                        {/* Chat Header */}
                                        <Box className="chat-support-chat-header">
                                            <Box display="flex" alignItems="center" gap={2}>
                                                {isMobile && (
                                                    <IconButton 
                                                        onClick={handleBackToList}
                                                        className="chat-support-back-button"
                                                    >
                                                        <ArrowBackIcon />
                                                    </IconButton>
                                                )}
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                                    variant="dot"
                                                >
                                                    <Avatar className="chat-support-chat-avatar">
                                                        {capitalizeFirstLetter(selectedChat.userName).charAt(0)}
                                                    </Avatar>
                                                </Badge>
                                                <Box>
                                                    <Typography 
                                                        variant="subtitle1"
                                                        className="chat-support-chat-user-name"
                                                    >
                                                        {capitalizeFirstLetter(selectedChat.userName)}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption"
                                                        className="chat-support-chat-user-email"
                                                    >
                                                        {selectedChat.userEmail || "User"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Messages Area */}
                                        <Box className="chat-support-messages-area">
                                            {messages.map((message) => (
                                                <Box
                                                    key={message.id}
                                                    className={`chat-support-message-container ${message.sender === "admin" ? "admin" : message.sender === "system" ? "system" : "user"}`}
                                                >
                                                    <Box
                                                        className={`chat-support-message-bubble ${message.sender === "admin" ? "" : message.sender === "system" ? "system" : "user"}`}
                                                    >
                                                        {/* Display attachments/images */}
                                                        {message.attachments && message.attachments.length > 0 && (
                                                            <Box style={{ marginBottom: message.text ? 8 : 0 }}>
                                                                {message.attachments.map((attachment, idx) => {
                                                                    let imageUrl = attachment.url || attachment;
                                                                    if (typeof imageUrl === 'string' && !imageUrl.startsWith('data:') && !imageUrl.startsWith('http')) {
                                                                        imageUrl = IMAGE_BASE + imageUrl;
                                                                    }
                                                                    return (
                                                                        <Box
                                                                            key={idx}
                                                                            className="chat-support-message-attachment"
                                                                        >
                                                                            {(attachment.type === 'image' || !attachment.type) && (
                                                                                <img
                                                                                    src={imageUrl}
                                                                                    alt={attachment.fileName || 'Image'}
                                                                                    className="chat-support-message-image"
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        )}
                                                        {message.text && (
                                                            <Typography 
                                                                variant="body2"
                                                                className="chat-support-message-text"
                                                            >
                                                                {message.text}
                                                            </Typography>
                                                        )}
                                                        {message.sender !== "system" && (
                                                            <Typography
                                                                variant="caption"
                                                                className="chat-support-message-time"
                                                            >
                                                                {fDateTime(message.timestamp)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </Box>

                                        {/* Message Input */}
                                        <Box className="chat-support-input-container">
                                            {imagePreview && (
                                                <Box className="chat-support-image-preview-container">
                                                    <Box className="chat-support-image-preview">
                                                        <img
                                                            src={imagePreview}
                                                            alt="Preview"
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleRemoveImage}
                                                            className="chat-support-image-remove-btn"
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            )}
                                            <Box className="chat-support-input-wrapper">
                                                {/* <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                                                    style={{ display: 'none' }}
                                                    onChange={handleImageSelect}
                                                />
                                                <IconButton
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="chat-support-attach-button"
                                                >
                                                    <AttachFileIcon />
                                                </IconButton> */}
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Type a message..."
                                                    value={messageInput}
                                                    onChange={(e) => setMessageInput(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === "Enter" && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSendMessage();
                                                        }
                                                    }}
                                                    multiline
                                                    maxRows={3}
                                                    className="chat-support-text-input"
                                                />
                                                <IconButton
                                                    color="primary"
                                                    onClick={handleSendMessage}
                                                    disabled={!messageInput.trim() && !selectedImage}
                                                    className="chat-support-send-button"
                                                >
                                                    <SendIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </>
                                ) : (
                                    <Box className="chat-support-select-conversation">
                                        <Box className="chat-support-empty-icon-container chat-support-empty-icon-container-small">
                                            <ChatBubbleOutlineIcon className="chat-support-empty-icon chat-support-empty-icon-small" />
                                        </Box>
                                        <Typography 
                                            variant="h5" 
                                            className="chat-support-empty-title"
                                        >
                                            Select a conversation to start chatting
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            className="chat-support-empty-text"
                                        >
                                            Choose from your existing conversations or wait for new messages
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
